import { Fragment, useMemo, useEffect } from "react";
import { Box, Stack, Typography, Breadcrumbs } from "@mui/material";
import { MoreVertRounded, ArrowForwardIosRounded, ImageRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { BRAND_COLORS } from "utility/brandColors";
import { useGetInstanceQuery } from "../../../../../../../../shell/services/accounts";
import { InstanceAvatar } from "../../../../../../../../shell/components/global-sidebar/components/InstanceAvatar";
import { useDomain } from "../../../../../../../../shell/hooks/use-domain";
import { AppState } from "../../../../../../../../shell/store/types";
import { useGetContentModelFieldsQuery } from "../../../../../../../../shell/services/instance";
import { useTranslation } from "react-i18next";
type GooglePreviewProps = {
  imageURL: string;
};
export const GooglePreview = ({
  imageURL
}: GooglePreviewProps) => {
  const {
    t
  } = useTranslation();
  const {
    modelZUID,
    itemZUID
  } = useParams<{
    modelZUID: string;
    itemZUID: string;
  }>();
  const {
    data: instance,
    isLoading: isLoadingInstance
  } = useGetInstanceQuery();
  const domain = useDomain();
  const location = useLocation();
  const isCreateItemPage = location?.pathname?.split("/")?.pop() === "new";
  const items = useSelector((state: AppState) => state.content);
  const item = items[isCreateItemPage ? `new:${modelZUID}` : itemZUID];
  const parent = items[item?.web?.parentZUID];
  const fullPathArray = useMemo(() => {
    let path: string[] = [domain];
    if (parent) {
      path = [...path, ...(parent.web?.path?.split("/") || [])];
    }

    // Remove empty strings
    return path.filter(i => !!i);
  }, [domain, parent, item?.web]);
  return <Stack direction="row" gap={2} p={2} bgcolor="common.white" borderRadius={2} border={1} borderColor="border" maxHeight={186} boxSizing="border-box" data-sentry-element="Stack" data-sentry-component="GooglePreview" data-sentry-source-file="GooglePreview.tsx">
      <Box flex={1} data-sentry-element="Box" data-sentry-source-file="GooglePreview.tsx">
        <Stack direction="row" gap={1.5} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="GooglePreview.tsx">
          <InstanceAvatar canUpdateAvatar={false} avatarSx={{
          width: 28,
          height: 28
        }} data-sentry-element="InstanceAvatar" data-sentry-source-file="GooglePreview.tsx" />
          <Box data-sentry-element="Box" data-sentry-source-file="GooglePreview.tsx">
            <Typography variant="body2" color="text.primary" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="GooglePreview.tsx">
              {instance?.name}
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr auto" gap={1.5} alignItems="center" data-sentry-element="Box" data-sentry-source-file="GooglePreview.tsx">
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              wordWrap: "break-word",
              wordBreak: "break-all",
              whiteSpace: "nowrap"
            }} data-sentry-element="Typography" data-sentry-source-file="GooglePreview.tsx">
                {fullPathArray.map((path, index) => <Fragment key={index}>
                    {path}
                    {index < fullPathArray?.length - 1 && <Box component="span" mx={0.75}>
                        ›
                      </Box>}
                  </Fragment>)}
              </Typography>
              <MoreVertRounded sx={{
              fontSize: "16px",
              fill: theme => theme.palette.text.secondary
            }} data-sentry-element="MoreVertRounded" data-sentry-source-file="GooglePreview.tsx" />
            </Box>
          </Box>
        </Stack>
        <Typography variant="h5" color={!!item?.web?.metaTitle ? BRAND_COLORS.google : "grey.500"} mt={1} mb={1.5} fontWeight={600} sx={{
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        wordWrap: "break-word",
        hyphens: "auto",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }} data-sentry-element="Typography" data-sentry-source-file="GooglePreview.tsx">
          {item?.web?.metaTitle || t("content.itemEditMetaTitle")}
        </Typography>
        <Typography variant="body2" color="grey.500" fontWeight={500} minHeight={39} sx={{
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        wordWrap: "break-word",
        hyphens: "auto",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }} data-sentry-element="Typography" data-sentry-source-file="GooglePreview.tsx">
          {item?.web?.metaDescription || t("content.itemEditMetaDescription")}
        </Typography>
      </Box>
      {!!imageURL ? <Box component="img" sx={{
      backgroundColor: theme => theme.palette.grey[100],
      objectFit: "cover"
    }} width={82} height={82} src={`${imageURL}?width=82&height=82&fit=cover`} flexShrink={0} borderRadius={2} /> : <Stack width={82} height={82} borderRadius={2} bgcolor="grey.100" justifyContent="center" alignItems="center" flexShrink={0}>
          <ImageRounded color="action" fontSize="large" />
        </Stack>}
    </Stack>;
};